import { useDispatch, useSelector } from 'react-redux';
import { alertActions, conversationsActions, conversationTagsActions } from '@actions';
import { conversationsService, conversationTagsService } from '@services';
import { handleApiError } from '@helpers/api';
import { useConversations, useTranslation } from '@hooks';
import { TAG_COLORS } from '@constants/conversation-tags';

export const useConversationTags = ({ setActiveConversation, filterRef }) => {
    const { data: catalog } = useSelector(state => state.conversationTags);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        updateConversationInFeedAndReturn,
        upsertConversationInFeed,
        isConversationMatchesFilter,
        conversationsRef,
    } = useConversations();

    const syncConversation = conversation => {
        updateConversationInFeedAndReturn(conversation, filterRef.current, true);
        setActiveConversation(prev => {
            if (prev?.id !== conversation.id) {
                return prev;
            }
            const merged = { ...prev, ...conversation };
            return isConversationMatchesFilter(merged, filterRef.current) ? merged : null;
        });
    };

    const patchTags = (conversationId, mutateTags) => {
        const previousRow = conversationsRef.current.find(c => c.id === conversationId);
        if (previousRow) {
            updateConversationInFeedAndReturn(
                { ...previousRow, tags: mutateTags(previousRow.tags || []) },
                filterRef.current,
                true,
            );
        }
        setActiveConversation(prev =>
            prev?.id === conversationId ? { ...prev, tags: mutateTags(prev.tags || []) } : prev,
        );
        return previousRow;
    };

    const rollbackTags = (conversationId, previousRow, restoreTags) => {
        const currentRow = conversationsRef.current.find(c => c.id === conversationId);
        if (currentRow) {
            updateConversationInFeedAndReturn(
                { ...currentRow, tags: restoreTags(currentRow.tags || []) },
                filterRef.current,
                true,
            );
        } else if (previousRow) {
            upsertConversationInFeed(previousRow, filterRef.current);
        }
        setActiveConversation(prev =>
            prev?.id === conversationId ? { ...prev, tags: restoreTags(prev.tags || []) } : prev,
        );
        dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
    };

    const assignTag = async (conversationId, tag) => {
        const previousRow = patchTags(conversationId, tags => [...tags.filter(x => x.id !== tag.id), tag]);
        try {
            const conversation = await conversationsService.addTag(conversationId, tag.id);
            syncConversation(conversation);
        } catch (error) {
            rollbackTags(conversationId, previousRow, tags => tags.filter(x => x.id !== tag.id));
            handleApiError(error);
        }
    };

    const addTag = async (conversationId, tagId) => {
        const tag = catalog.find(x => x.id === tagId);
        if (!tag) {
            return;
        }
        await assignTag(conversationId, tag);
    };

    const removeTag = async (conversationId, tagId) => {
        const previousRow = patchTags(conversationId, tags => tags.filter(x => x.id !== tagId));
        const removedTag = (previousRow?.tags || []).find(x => x.id === tagId) || catalog.find(x => x.id === tagId);
        try {
            const conversation = await conversationsService.removeTag(conversationId, tagId);
            syncConversation(conversation);
        } catch (error) {
            rollbackTags(conversationId, previousRow, tags =>
                removedTag ? [...tags.filter(x => x.id !== tagId), removedTag] : tags,
            );
            handleApiError(error);
        }
    };

    const createAndAssignTag = async (conversationId, name) => {
        const existing = catalog.find(tag => tag.name.toLowerCase() === name.toLowerCase());
        if (existing) {
            await assignTag(conversationId, existing);
            return;
        }
        try {
            const color = TAG_COLORS[catalog.length % TAG_COLORS.length];
            const [created] = await conversationTagsService.addOrUpdateTags([{ name, color }]);
            dispatch(conversationTagsActions.get());
            await assignTag(conversationId, created);
        } catch (error) {
            handleApiError(error);
        }
    };

    const updateTag = async tag => {
        try {
            await conversationTagsService.addOrUpdateTags([tag]);
            dispatch(conversationTagsActions.get());
            dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            return true;
        } catch (error) {
            handleApiError(error);
            return false;
        }
    };

    const deleteTag = async tagId => {
        try {
            await conversationTagsService.deleteTag(tagId);
            dispatch(conversationTagsActions.get());
            dispatch(conversationsActions.getCounters(filterRef.current));
            dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
            setActiveConversation(prev =>
                prev ? { ...prev, tags: (prev.tags || []).filter(tag => tag.id !== tagId) } : prev,
            );
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (error) {
            handleApiError(error);
        }
    };

    return { addTag, removeTag, createAndAssignTag, updateTag, deleteTag };
};
